import { useState } from 'react';
import { compose } from 'redux';
import { useQuery as useReactQuery } from 'react-query';
import {
  isGuideOrPacket,
  bookCreditsContentTitle,
  bookCreditsContentDescription,
  isGuide,
} from 'modules/v2/pages/Dentist/OrderGuideModal/constants';
import {
  fetchUserCredits,
  getGuideCatalog,
  getGuidesOrders,
  getGuideOnboardData,
} from 'modules/api';
import { withAuth } from 'modules/auth/containers';
import { pluralize } from 'modules/v2/common/utils';
import {
  SecondaryBlackButton,
  Modal,
  Loader,
} from 'modules/v2/common/components';
import { DentistSideNav } from 'modules/v2/components';
import { DashboardTemplate } from 'modules/v2/templates';
import {
  withBooksStore,
  withProfile,
  withUserCredits,
  withReleaseNotes,
} from 'modules/v2/containers';
import OrderGuideModal from 'modules/v2/pages/Dentist/OrderGuideModal';
import { goToMyOrders } from 'modules/v2/draft/store';
import S from './styles';
import { GuideGridPreview } from './GuideGridPreview';

/**
 * DO NOT migrate this screen's credit read to the DS print ledger.
 *
 * The number here is DENTIST GUIDE credits. The legacy BBA endpoint
 * `GET /user/credits` returns one polymorphic scalar —
 * `accountType === 'Dentist' ? dentist_guide_credits : afy_book_credits` — and
 * this page is only reachable by Dentist accounts, so `data.data.credits` is
 * guide credits here.
 *
 * The DS ledger tracks PRINT credits only (`LedgerCreditType.Print` is its sole
 * member) and guide credits are a deliberate, permanent exclusion
 * (universal-credits ADR-003). Pointing this at `usePrintCreditsBalance` would
 * render a fabricated balance to a paying member.
 */
const GuideCatalog = () => {
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [guidePacketName, setGuidePacketName] = useState('guide');
  // For getting latest ordered Data for pre filling Guide Forms
  const currentOrderPage = 1;
  const perPage = 1;
  const { data: guidesOrders } = useReactQuery(['getGuidesOrders', currentOrderPage, perPage], () =>
    getGuidesOrders(currentOrderPage, perPage),
  );
  const { data: guidesOnboardData } = useReactQuery('guidesOnboardData', getGuideOnboardData);

  const guidePrefillFormData = guidesOnboardData || guidesOrders?.data[0] || {};

  // Handle Reorder Flow
  const [payloadData, setPayloadData] = useState({
    practiceName: '',
    practicePhone: '',
    practiceEmail: '',
    practiceLogo: '',
    practiceWebsite: '',
    quantity: 0,
    guideName: '',
    guideId: '',
    thumbnail: '',
    landingPage: '',
    frontCover: [],
    practiceAddress: {
      addressLine1: '',
      city: '',
      state: '',
      pincode: '',
      country: '',
      email: '',
      firstName: '',
      lastName: '',
    },
    shippingAddress: {
      addressLine1: '',
      city: '',
      state: '',
      pincode: '',
      country: '',
      email: '',
      firstName: '',
      lastName: '',
    },
  });

  let creditsSubHeader = <S.Loading>Loading credits...</S.Loading>;
  let content = <Loader height="calc(100vh - 400px)" title="Loading Guide Catalog" />;

  const { data: userBookCredit, isLoading: isCreditLoading } = useReactQuery(
    ['getUserCredits'],
    fetchUserCredits,
  );
  const bookCredit = userBookCredit?.data?.data?.credits;

  const { data: guides, isLoading: isGettingGuides } = useReactQuery(
    'getGuideCatalog',
    getGuideCatalog,
  );

  const handleCancelPublish = () => {
    setPublishModalOpen(false);
  };

  const handleClosePublish = () => {
    setPublishModalOpen(false);
  };

  const handleStartOrder = (item) => {
    setGuidePacketName(isGuideOrPacket(item.guideId));

    const isCreditLow = isGuide(item.guideId) ? bookCredit < 10 : bookCredit < 30;
    setIsWarningModalOpen(isCreditLow);

    if (!isCreditLow) {
      setPayloadData({
        ...payloadData,
        guideName: item.guideName,
        thumbnail: item.thumbnail,
        guideId: item.guideId,
      });
      setPublishModalOpen(true);
    }
  };

  if (!isCreditLoading) {
    creditsSubHeader = (
      <S.Credits isCreditEnough={bookCredit >= 8}>
        {pluralize('Credit', 'Credits', bookCredit)}
      </S.Credits>
    );
  }

  if (!isGettingGuides) {
    content = <GuideGridPreview guides={guides} handleStartOrder={handleStartOrder} />;
  }

  const subHeader = (
    <S.SubHeader>
      <S.SubHeaderWrapper>
        <S.CreditsWrapper>{creditsSubHeader}</S.CreditsWrapper>
      </S.SubHeaderWrapper>
    </S.SubHeader>
  );

  return (
    <DashboardTemplate sidebar={<DentistSideNav />} contentTitle="Guide Catalog" hasSidebar>
      {subHeader}
      {content}

      <Modal
        isOpen={isWarningModalOpen}
        maskClosable
        title="Warning"
        width="auto"
        footer={[
          <S.ModalFooterContainer key={1}>
            <SecondaryBlackButton
              style={{ width: '80%' }}
              onClick={() => setIsWarningModalOpen(false)}
            >
              Cancel, I'll buy later
            </SecondaryBlackButton>
          </S.ModalFooterContainer>,
        ]}
      >
        <S.ContentContainer>
          <S.ContentTitle>
            You have <span>{bookCredit} credits</span> left
          </S.ContentTitle>
          <S.ContentDescription>{bookCreditsContentTitle(guidePacketName)}</S.ContentDescription>
          <S.ContentDescription>
            {bookCreditsContentDescription(guidePacketName)}
          </S.ContentDescription>
        </S.ContentContainer>
      </Modal>
      {publishModalOpen && (
        <OrderGuideModal
          isOpen
          draftId="draftId"
          title={`Title: ${payloadData.guideName.toUpperCase()}`}
          onCancel={handleCancelPublish}
          onClose={handleClosePublish}
          setPayloadData={setPayloadData}
          payloadData={payloadData}
          onComplete={goToMyOrders}
          guidePrefillFormData={guidePrefillFormData}
          fromScreen="GuideCatalog"
          guidePacketName={guidePacketName}
        />
      )}
    </DashboardTemplate>
  );
};

export default compose(
  withAuth,
  withProfile,
  withUserCredits,
  withBooksStore,
  withReleaseNotes,
)(GuideCatalog);
